import CancelSharpIcon from "@mui/icons-material/CancelSharp";
import {
  Box,
  Button,
  Chip,
  FormControl,
  FormControlLabel,
  FormLabel,
  MenuItem,
  Select,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import moment from "moment";
import React, { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { toast } from "react-toastify";
import { getAllServices } from "../../../api/services/api";
import {
  Coupon,
  CouponScope,
  CouponType,
  CreateCouponPayload,
  couponService,
} from "../../../api/services/couponService";
import {
  ClinicianOption,
  ClinicianSearchAutocomplete,
  useCliniciansByIds,
} from "./ClinicianSearchAutocomplete";
import {
  PatientOption,
  PatientSearchAutocomplete,
  patientOptionLabel,
} from "./PatientSearchAutocomplete";

interface CouponFormProps {
  formData: Coupon | null;
  toggleDrawer: (open: boolean) => void;
}

interface FormValues {
  code: string;
  title_en: string;
  title_ar: string;
  description_en: string;
  description_ar: string;
  type: CouponType;
  value: number;
  min_order_amount: string;
  max_discount_amount: string;
  usage_limit: string;
  usage_per_user: string;
  starts_at: string;
  ends_at: string;
  is_active: boolean;
  is_public: boolean;
  scope: CouponScope;
}

const toDatetimeLocal = (iso?: string | null) => {
  if (!iso) return "";
  return moment(iso).format("YYYY-MM-DDTHH:mm");
};

const toIsoOrNull = (value?: string) => {
  if (!value) return null;
  return moment(value).toISOString();
};

const optionalNumber = (value?: string) => {
  if (value === undefined || value === null || value === "") return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
};

export const CouponForm = ({ formData, toggleDrawer }: CouponFormProps) => {
  const queryClient = useQueryClient();
  const isEdit = Boolean(formData?.id);

  const [serviceIds, setServiceIds] = useState<number[]>(
    formData?.service_ids || []
  );
  const [selectedClinicians, setSelectedClinicians] = useState<
    ClinicianOption[]
  >([]);
  const [selectedPatients, setSelectedPatients] = useState<PatientOption[]>(
    []
  );
  const [existingAssignments, setExistingAssignments] = useState(
    formData?.assignments || []
  );

  const clinicianIdsForHydrate = formData?.clinician_ids || [];
  const { data: hydratedClinicians } = useCliniciansByIds(
    clinicianIdsForHydrate
  );

  const { data: servicesData } = useQuery(
    ["services", { limit: 1000 }],
    getAllServices
  );

  const services =
    servicesData?.data?.services || servicesData?.data?.data || [];

  const buildFormValues = (data: Coupon | null): FormValues => ({
    code: data?.code || "",
    title_en: data?.title_en || "",
    title_ar: data?.title_ar || "",
    description_en: data?.description_en || "",
    description_ar: data?.description_ar || "",
    type: data?.type || "PERCENTAGE",
    value: data?.value ?? 10,
    min_order_amount:
      data?.min_order_amount != null ? String(data.min_order_amount) : "",
    max_discount_amount:
      data?.max_discount_amount != null
        ? String(data.max_discount_amount)
        : "",
    usage_limit: data?.usage_limit != null ? String(data.usage_limit) : "",
    usage_per_user:
      data?.usage_per_user != null ? String(data.usage_per_user) : "",
    starts_at: toDatetimeLocal(data?.starts_at),
    ends_at: toDatetimeLocal(data?.ends_at),
    is_active: data?.is_active ?? true,
    is_public: data?.is_public ?? true,
    scope: data?.scope || "ALL",
  });

  const {
    register,
    handleSubmit,
    control,
    watch,
    reset,
    formState: { errors },
  } = useForm<FormValues>({
    defaultValues: buildFormValues(formData),
  });

  useEffect(() => {
    setServiceIds(formData?.service_ids || []);
    setExistingAssignments(formData?.assignments || []);
    setSelectedPatients([]);
    if (!formData?.clinician_ids?.length) {
      setSelectedClinicians([]);
    }
    reset(buildFormValues(formData));
  }, [formData, reset]);

  useEffect(() => {
    if (!hydratedClinicians) return;
    setSelectedClinicians(hydratedClinicians);
  }, [hydratedClinicians]);

  const couponType = watch("type");
  const scope = watch("scope");
  const isPublic = watch("is_public");

  const invalidate = () => queryClient.invalidateQueries("coupons");

  const { mutateAsync: createCoupon, isLoading: creating } = useMutation(
    (payload: CreateCouponPayload) => couponService.create(payload)
  );
  const { mutateAsync: updateCoupon, isLoading: updating } = useMutation(
    ({ id, payload }: { id: number; payload: CreateCouponPayload }) =>
      couponService.update(id, payload)
  );
  const { mutateAsync: assignPatients, isLoading: assigning } = useMutation(
    ({ id, patientIds }: { id: number; patientIds: number[] }) =>
      couponService.assignPatients(id, patientIds)
  );
  const { mutate: unassignPatient, isLoading: unassigning } = useMutation(
    ({ id, patientId }: { id: number; patientId: number }) =>
      couponService.unassignPatient(id, patientId),
    {
      onSuccess: (_res, vars) => {
        setExistingAssignments((prev) =>
          prev.filter((a) => a.patient_id !== vars.patientId)
        );
        invalidate();
        toast.success("Patient unassigned");
      },
      onError: (res: any) => {
        toast.error(res?.response?.data?.message || "Failed to unassign");
      },
    }
  );

  const buildPayload = (data: FormValues): CreateCouponPayload => {
    const payload: CreateCouponPayload = {
      code: data.code.trim().toUpperCase(),
      title_en: data.title_en.trim(),
      title_ar: data.title_ar?.trim() || undefined,
      description_en: data.description_en?.trim() || undefined,
      description_ar: data.description_ar?.trim() || undefined,
      type: data.type,
      value: Number(data.value),
      min_order_amount: optionalNumber(data.min_order_amount),
      max_discount_amount: optionalNumber(data.max_discount_amount),
      usage_limit: optionalNumber(data.usage_limit),
      usage_per_user: optionalNumber(data.usage_per_user),
      starts_at: toIsoOrNull(data.starts_at),
      ends_at: toIsoOrNull(data.ends_at),
      is_active: data.is_active,
      is_public: data.is_public,
      scope: data.scope,
      service_ids: data.scope === "SERVICE" ? serviceIds : [],
      clinician_ids:
        data.scope === "CLINICIAN"
          ? selectedClinicians.map((c) => c.id)
          : [],
    };
    return payload;
  };

  const onSubmit = async (data: FormValues) => {
    try {
      if (data.scope === "CLINICIAN" && selectedClinicians.length === 0) {
        toast.error("Select at least one clinician for clinician-scoped coupons");
        return;
      }
      if (data.scope === "SERVICE" && serviceIds.length === 0) {
        toast.error("Select at least one service for service-scoped coupons");
        return;
      }
      const payload = buildPayload(data);
      let couponId = formData?.id;

      if (isEdit && couponId) {
        await updateCoupon({ id: couponId, payload });
        toast.success("Coupon updated");
      } else {
        const res = await createCoupon(payload);
        couponId = res.data.data.id;
        toast.success("Coupon created");
      }

      const patientIds = selectedPatients
        .map((p) => p?.id)
        .filter((id): id is number => typeof id === "number");

      if (couponId && patientIds.length > 0) {
        await assignPatients({ id: couponId, patientIds });
        toast.success(`Assigned ${patientIds.length} patient(s)`);
      }

      invalidate();
      toggleDrawer(false);
    } catch (err: any) {
      toast.error(
        err?.response?.data?.message || "Failed to save coupon"
      );
    }
  };

  const assignmentLabel = (a: (typeof existingAssignments)[0]) => {
    if (a.patient) {
      return patientOptionLabel({
        id: a.patient_id,
        users: a.patient.users as PatientOption["users"],
      });
    }
    return `Patient #${a.patient_id}`;
  };

  const isSaving = creating || updating || assigning;

  return (
    <Box width="480px" pb={4}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <TextField
          label="Coupon Code*"
          fullWidth
          variant="standard"
          inputProps={{ style: { textTransform: "uppercase" } }}
          {...register("code", {
            required: "Code is required",
            minLength: { value: 2, message: "Min 2 characters" },
          })}
          error={Boolean(errors.code)}
          helperText={errors.code?.message}
        />

        <TextField
          label="Title (English)*"
          sx={{ mt: 1.5 }}
          fullWidth
          variant="standard"
          {...register("title_en", { required: "Title is required" })}
          error={Boolean(errors.title_en)}
          helperText={errors.title_en?.message}
        />

        <TextField
          label="Title (Arabic)"
          sx={{ mt: 1.5 }}
          fullWidth
          variant="standard"
          {...register("title_ar")}
        />

        <TextField
          label="Description (English)"
          sx={{ mt: 1.5 }}
          fullWidth
          multiline
          maxRows={3}
          variant="standard"
          {...register("description_en")}
        />

        <TextField
          label="Description (Arabic)"
          sx={{ mt: 1.5 }}
          fullWidth
          multiline
          maxRows={3}
          variant="standard"
          {...register("description_ar")}
        />

        <Controller
          name="type"
          control={control}
          render={({ field }) => (
            <TextField
              select
              label="Discount Type*"
              sx={{ mt: 1.5 }}
              fullWidth
              variant="standard"
              {...field}
            >
              <MenuItem value="PERCENTAGE">Percentage (%)</MenuItem>
              <MenuItem value="FIXED">Fixed (SAR)</MenuItem>
            </TextField>
          )}
        />

        <TextField
          label={
            couponType === "PERCENTAGE"
              ? "Discount Value (%)*"
              : "Discount Value (SAR)*"
          }
          sx={{ mt: 1.5 }}
          fullWidth
          type="number"
          variant="standard"
          inputProps={{
            min: 0,
            max: couponType === "PERCENTAGE" ? 100 : undefined,
            step: "any",
          }}
          {...register("value", {
            required: "Value is required",
            valueAsNumber: true,
            min: { value: 0, message: "Must be ≥ 0" },
            validate: (v) =>
              couponType !== "PERCENTAGE" || v <= 100 || "Max 100%",
          })}
          error={Boolean(errors.value)}
          helperText={
            errors.value?.message ||
            (couponType === "PERCENTAGE" && Number(watch("value")) >= 100
              ? "100% coupons waive at most 1 timeslot per booking (remaining slots stay payable)."
              : undefined)
          }
        />

        <TextField
          label="Min Order Amount (SAR)"
          sx={{ mt: 1.5 }}
          fullWidth
          type="number"
          variant="standard"
          inputProps={{ min: 0, step: "any" }}
          {...register("min_order_amount")}
        />

        {couponType === "PERCENTAGE" ? (
          <TextField
            label="Max Discount Cap (SAR)"
            sx={{ mt: 1.5 }}
            fullWidth
            type="number"
            variant="standard"
            inputProps={{ min: 0, step: "any" }}
            {...register("max_discount_amount")}
          />
        ) : null}

        <TextField
          label="Global Usage Limit"
          sx={{ mt: 1.5 }}
          fullWidth
          type="number"
          variant="standard"
          inputProps={{ min: 1, step: 1 }}
          placeholder="Leave empty for unlimited"
          {...register("usage_limit")}
        />

        <TextField
          label="Per-User Usage Limit"
          sx={{ mt: 1.5 }}
          fullWidth
          type="number"
          variant="standard"
          inputProps={{ min: 1, step: 1 }}
          placeholder="Leave empty for unlimited"
          {...register("usage_per_user")}
        />

        <TextField
          label="Starts At"
          sx={{ mt: 1.5 }}
          fullWidth
          type="datetime-local"
          variant="standard"
          InputLabelProps={{ shrink: true }}
          {...register("starts_at")}
        />

        <TextField
          label="Ends At"
          sx={{ mt: 1.5 }}
          fullWidth
          type="datetime-local"
          variant="standard"
          InputLabelProps={{ shrink: true }}
          {...register("ends_at")}
        />

        <Controller
          name="scope"
          control={control}
          render={({ field }) => (
            <TextField
              select
              label="Scope"
              sx={{ mt: 1.5 }}
              fullWidth
              variant="standard"
              {...field}
            >
              <MenuItem value="ALL">All services & clinicians</MenuItem>
              <MenuItem value="SERVICE">Specific services</MenuItem>
              <MenuItem value="CLINICIAN">Specific clinicians</MenuItem>
            </TextField>
          )}
        />

        {scope === "SERVICE" ? (
          <FormControl fullWidth sx={{ mt: 1.5 }}>
            <FormLabel>Services</FormLabel>
            <Select
              multiple
              variant="standard"
              value={serviceIds}
              onChange={(e) =>
                setServiceIds(
                  typeof e.target.value === "string"
                    ? []
                    : (e.target.value as number[])
                )
              }
              renderValue={(selected) => (
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                  {(selected as number[]).map((id) => {
                    const svc = services.find((s: any) => s.id === id);
                    return (
                      <Chip
                        key={id}
                        size="small"
                        label={svc?.title || svc?.name || id}
                      />
                    );
                  })}
                </Box>
              )}
            >
              {services.map((svc: any) => (
                <MenuItem key={svc.id} value={svc.id}>
                  {svc.title || svc.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        ) : null}

        {scope === "CLINICIAN" ? (
          <Box sx={{ mt: 1.5 }}>
            <ClinicianSearchAutocomplete
              value={selectedClinicians}
              onChange={setSelectedClinicians}
              label="Clinicians*"
              placeholder="Search by name, email, phone, or ID…"
              helperText="Coupon only applies when booking these clinicians."
            />
          </Box>
        ) : null}

        <Box sx={{ mt: 2, display: "flex", flexDirection: "column", gap: 0.5 }}>
          <Controller
            name="is_active"
            control={control}
            render={({ field }) => (
              <FormControlLabel
                control={
                  <Switch
                    checked={field.value}
                    onChange={(e) => field.onChange(e.target.checked)}
                  />
                }
                label="Active"
              />
            )}
          />
          <Controller
            name="is_public"
            control={control}
            render={({ field }) => (
              <FormControlLabel
                control={
                  <Switch
                    checked={field.value}
                    onChange={(e) => field.onChange(e.target.checked)}
                  />
                }
                label="Public (discoverable to all patients)"
              />
            )}
          />
        </Box>

        {!isPublic ? (
          <Typography
            sx={{ mt: 1, mb: 0.5, fontSize: 13, color: "text.secondary" }}
          >
            Private coupons require patient assignments to be redeemable.
          </Typography>
        ) : null}

        {isEdit && existingAssignments.length > 0 ? (
          <Box sx={{ mt: 2 }}>
            <FormLabel>Assigned Patients</FormLabel>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mt: 1 }}>
              {existingAssignments.map((a) => (
                <Chip
                  key={a.id}
                  label={assignmentLabel(a)}
                  onDelete={() =>
                    unassignPatient({
                      id: formData!.id,
                      patientId: a.patient_id,
                    })
                  }
                  deleteIcon={
                    <CancelSharpIcon
                      onMouseDown={(e) => e.stopPropagation()}
                    />
                  }
                  disabled={unassigning}
                />
              ))}
            </Box>
          </Box>
        ) : null}

        <Box sx={{ mt: 2 }}>
          <PatientSearchAutocomplete
            multiple
            value={selectedPatients}
            onChange={setSelectedPatients}
            label={
              isEdit ? "Assign more patients" : "Assign patients (optional)"
            }
            placeholder="Search by name, email, phone, or ID…"
            helperText="Type to search patients, then select to assign."
          />
        </Box>

        <Button
          type="submit"
          sx={{ mt: 3 }}
          fullWidth
          variant="contained"
          disabled={isSaving}
        >
          {isSaving ? "Saving..." : "Submit"}
        </Button>
      </form>
    </Box>
  );
};
